import { isAxiosError } from "axios";
import i18n, { t } from "i18next";
import axios from "../lib/axios";
import { Regulation } from "../types";

export async function signIn(
  email: string,
  password: string,
  companyId: number,
  rememberMe: boolean,
) {
  try {
    const response = await axios.post(
      "/api/auth/login",
      {
        email,
        password,
        companyId,
        rememberMe,
      },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return {
      isOk: true,
      data: {
        email,
        id: response.data.id,
        companyId: response.data.companyId,
        requiresTwoFactorAuthentication:
          response.data.requiresTwoFactorAuthentication,
        isLockedOut: response.data.isLockedOut,
        confirmationRequired: response.data.confirmationRequired,
        peselLoginBlock: response.data.peselLoginBlock,
        firstChangePassword: response.data.firstChangePassword,
        enable2Fa: response.data.enable2Fa,
      },
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function getUser() {
  try {
    const response = await axios.get<{
      id: number;
      email: string;
      companyId: number;
      requiresTwoFactorAuthentication?: boolean;
      termsOfUseAccepted: boolean;
      generalDataProtectionRegulationAccepted: boolean;
      processingOfEmailAddressAccepted: boolean;
    }>("/api/auth/user", {
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function createAccount(
  companyId: number,
  employeeId: number,
  contractType: number,
  role: string,
  email: string,
  password: string,
  managerId?: number,
  managerCompanyId?: number,
): Promise<string | undefined> {
  try {
    const response = await axios.post<{ message?: string }>(
      "/api/users",
      {
        companyId,
        employeeId,
        contractType,
        role,
        managerId,
        managerCompanyId,
        email,
        password,
      },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return response.data?.message;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
}

export async function changePassword(password: string, pesel: string) {
  try {
    const response = await axios.post(
      "/api/auth/change-password-by-user",
      {
        password,
        pesel,
      },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return {
      isOk: true,
      message: response.data.message || t("Password reset successfully!"),
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function changePasswordFirstLogin(
  companyId: number,
  email: string,
  password: string,
) {
  try {
    await axios.post(
      "/api/auth/change-password-by-user-first-login",
      { email, password, companyId },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          } else if ("error" in error.response.data) {
            message = error.response.data.error;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }

      throw new Error(message);
    }
  }
}

export async function changePasswordAdmin(userId: number, password: string) {
  try {
    const response = await axios.post(
      "/api/auth/change-password-by-administrator",
      {
        userId,
        password,
      },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return {
      isOk: true,
      message: response.data.message || t("Password reset successfully!"),
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function twoFaLogin(code: string, rememberMe: boolean) {
  try {
    const response = await axios.post(
      "/api/auth/login-with-2fa",
      {
        code,
        rememberMe,
      },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return {
      isOk: true,
      messsage: response.data.message || t("Login successfully!"),
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function loginWithRecoveryCode(recoveryCode: string) {
  try {
    const response = await axios.post(
      "/api/auth/login-with-recovery-code",
      {
        recoveryCode,
      },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return {
      isOk: true,
      messsage: response.data.message || t("Login successfully!"),
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function getUserData() {
  try {
    const response = await axios.get<{
      id: number;
      email: string;
      companyId: number;
      roles: string[];
      employeeId: number;
      contractType: number;
      isTimeManager: boolean;
      requiresTwoFactorAuthentication?: boolean;
      termsOfUseAccepted: boolean;
      generalDataProtectionRegulationAccepted: boolean;
      processingOfEmailAddressAccepted: boolean;
    }>("/api/auth/user", {
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function lockOutDisableLogin(
  email: string,
  pesel: string,
  companyId: number,
) {
  try {
    const response = await axios.post(
      "/api/auth/lockout-disable",
      {
        email,
        pesel,
        companyId,
      },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return {
      isOk: true,
      messsage: response.data.message || t("Account lockout sucessfull."),
      data: {
        peselLoginBlock: response.data.peselLoginBlock,
      },
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function firstLogin(
  email: string,
  pesel: string,
  companyId: number,
) {
  try {
    const response = await axios.post(
      "/api/auth/confirmation",
      {
        email,
        pesel,
        companyId,
      },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );
    return {
      isOk: true,
      messsage: response.data.message || t("Login successfully!"),
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function changeEmailAdmin(userId: number, email: string) {
  try {
    const response = await axios.post(
      "/api/auth/change-email-by-administrator",
      {
        userId,
        email,
      },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return {
      isOk: true,
      message: response.data.message || t("Email changed successfully!"),
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function subordinateEmployee(
  companyId: number,
  employeeId: number,
) {
  try {
    const response = await axios.get(`/api/manager/subordinates`, {
      params: {
        companyId,
        employeeId,
      },
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      message:
        response.data.message ||
        t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

// Podwładni przypisani do kierownika na stałe (UserManagerRelations) - bez wypożyczeń z giełdy
// i zastępstw. Używane w panelu administracyjnym, gdzie usuwa się wyłącznie stałe przypisania.
export async function directSubordinateEmployee(
  companyId: number,
  employeeId: number,
) {
  try {
    const response = await axios.get(`/api/manager/direct-subordinates`, {
      params: {
        companyId,
        employeeId,
      },
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      message:
        response.data.message ||
        t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function deleteManagers(
  companyId: number,
  employeeId: number,
  managerId: number,
  managerCompanyId: number,
) {
  try {
    const response = await axios.delete(`/api/manager/delete-manager`, {
      data: {
        companyId,
        employeeId,
        managerId,
        managerCompanyId,
      },
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      message: response.data.message || t("User deleted successfully!"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function deleteSubordinateEmployees(
  companyId: number,
  employeeId: number,
  managerId: number,
  managerCompanyId: number,
) {
  try {
    const response = await axios.delete(`/api/manager/delete-subordinates`, {
      data: {
        companyId,
        employeeId,
        managerId,
        managerCompanyId,
      },
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      message: response.data.message || t("User deleted successfully!"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function addSubordinate(
  entries: { companyId: number; employeeId: number }[],
  managerId: number,
  managerCompanyId: number,
) {
  try {
    const response = await axios.post(
      `/api/manager/add-subordinates`,
      {
        entries,
        managerEmployeeId: managerId,
        managerCompanyId,
      },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return {
      isOk: true,
      message: response.data.message || t("User added succesfull!"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function addManagerr(
  companyId: number,
  employeeId: number,
  managerId: number,
  managerCompanyId: number,
) {
  try {
    const response = await axios.post(
      `/api/change-user-manager`,
      {
        companyId,
        employeeId,
        managerId,
        managerCompanyId,
      },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return {
      isOk: true,
      message: response.data.message || t("User added succesfull!"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function getEmployeesWithoutManager(companyId: number) {
  try {
    const response = await axios.get(`/api/manager/employee-without-manager`, {
      params: {
        companyId,
      },
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      message:
        response.data.message ||
        t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function disactiveUserAccount(
  employeeId: number,
  companyId: number,
) {
  try {
    const response = await axios.put(
      `/api/user-deactivated`,
      {
        employeeId,
        companyId,
      },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return {
      isOk: true,
      message:
        response.data.message ||
        t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export const acceptRegulation = async (
  regulation: Regulation,
): Promise<void> => {
  try {
    await axios.post("/api/auth/accept-regulation", null, {
      headers: {
        "Accept-Language": i18n.language,
      },
      params: {
        regulation: regulation,
      },
    });
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};
